import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, MapPin, Navigation, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { geolocationErrorMessage, getCurrentPosition, isShortGoogleMapsLink, parseLatLngFromMapsUrl } from '../lib/geo';
import { useRegions, useShifts } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type Branch = {
  id: string; name: string; code: string; regionId: string; defaultShiftId: string | null; address: string; state: string | null; contactPhone: string | null;
  geoLat: string | null; geoLng: string | null; geoRadiusM: number; isActive: boolean;
};

function LocationCapture({ lat, lng, onCapture }: { lat: string; lng: string; onCapture: (lat: string, lng: string) => void }) {
  const [locating, setLocating] = useState(false);
  const [mapsLink, setMapsLink] = useState('');
  const [resolving, setResolving] = useState(false);
  const { success, error: toastError } = useToast();

  async function capture() {
    setLocating(true);
    try {
      const pos = await getCurrentPosition();
      onCapture(pos.coords.latitude.toFixed(6), pos.coords.longitude.toFixed(6));
    } catch (err) {
      toastError(geolocationErrorMessage(err));
    } finally {
      setLocating(false);
    }
  }

  async function useMapsLink() {
    const url = mapsLink.trim();
    if (!url) return;
    const direct = parseLatLngFromMapsUrl(url);
    if (direct) { onCapture(direct.lat, direct.lng); setMapsLink(''); success('Location set from link'); return; }
    if (!isShortGoogleMapsLink(url)) { toastError("Couldn't find coordinates in that link — try pasting the full Google Maps URL instead."); return; }
    setResolving(true);
    try {
      const { data } = await api.get<{ resolvedUrl: string }>('/api/geo/resolve-maps-link', { params: { url } });
      const resolved = parseLatLngFromMapsUrl(data.resolvedUrl);
      if (!resolved) { toastError("Couldn't find coordinates in that link."); return; }
      onCapture(resolved.lat, resolved.lng);
      setMapsLink('');
      success('Location set from link');
    } catch (err) {
      toastError(apiErrorMessage(err, "Couldn't resolve that link"));
    } finally {
      setResolving(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-canvas/50 p-4 sm:col-span-2">
      <p className="text-xs font-medium text-muted">Branch location (for the check-in geofence)</p>
      <p className="mt-1 text-sm">{lat && lng ? `${lat}, ${lng}` : 'No location captured yet'}</p>
      <button type="button" className="btn-secondary mt-3" disabled={locating} onClick={() => void capture()}>
        <Navigation size={16} /> {locating ? 'Locating…' : 'Use current location'}
      </button>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted"><MapPin size={14} className="mt-0.5 shrink-0" /> Stand at the branch entrance for best accuracy.</p>

      <div className="mt-4 flex items-end gap-2 border-t border-border pt-4">
        <label className="label flex-1">Or paste a Google Maps link<input className="field mt-1" placeholder="https://maps.google.com/…" value={mapsLink} onChange={(e) => setMapsLink(e.target.value)} /></label>
        <button type="button" className="btn-secondary" disabled={!mapsLink.trim() || resolving} onClick={() => void useMapsLink()}>
          <Link2 size={16} /> {resolving ? 'Resolving…' : 'Use link'}
        </button>
      </div>
    </div>
  );
}

function BranchForm({ initial, onSubmit, onCancel, pending }: { initial?: Branch; onSubmit: (v: Record<string, unknown>) => void; onCancel: () => void; pending: boolean }) {
  const regions = useRegions();
  const shifts = useShifts();
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [regionId, setRegionId] = useState(initial?.regionId ?? '');
  const [defaultShiftId, setDefaultShiftId] = useState(initial?.defaultShiftId ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [geoLat, setGeoLat] = useState(initial?.geoLat ?? '');
  const [geoLng, setGeoLng] = useState(initial?.geoLng ?? '');
  const [geoRadiusM, setGeoRadiusM] = useState(String(initial?.geoRadiusM ?? 150));
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ name, code: code.toUpperCase(), regionId, defaultShiftId: defaultShiftId || null, address, state: state || null, contactPhone: contactPhone || null, geoLat: geoLat || null, geoLng: geoLng || null, geoRadiusM: Number(geoRadiusM), isActive });
  }

  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={submit}>
      <label className="label">Branch name<input className="field mt-1" required placeholder="e.g. Rajkot Main" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Short code<input className="field mt-1" required maxLength={20} placeholder="e.g. RJK01" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></label>
      <label className="label">Region<Select className="mt-1" required value={regionId} onChange={(e) => setRegionId(e.target.value)}><option value="">Select…</option>{regions.data?.filter((r) => r.isActive).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></label>
      <label className="label">Default shift<Select className="mt-1" value={defaultShiftId} onChange={(e) => setDefaultShiftId(e.target.value)}><option value="">None</option>{shifts.data?.filter((s) => s.isActive).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></label>
      <label className="label sm:col-span-2">Address<input className="field mt-1" required value={address} onChange={(e) => setAddress(e.target.value)} /></label>
      <label className="label">State<input className="field mt-1" placeholder="e.g. Gujarat" value={state} onChange={(e) => setState(e.target.value)} /><span className="mt-1 block text-xs text-muted">Drives Professional Tax and LWF for staff at this branch.</span></label>
      <label className="label">Contact phone<input className="field mt-1" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></label>
      <label className="label">Check-in radius (metres)<input type="number" min={25} max={5000} className="field mt-1" value={geoRadiusM} onChange={(e) => setGeoRadiusM(e.target.value)} /></label>

      <LocationCapture lat={geoLat} lng={geoLng} onCapture={(lat, lng) => { setGeoLat(lat); setGeoLng(lng); }} />

      {initial && <label className="label flex items-center gap-3 sm:col-span-2">Active<Switch checked={isActive} onChange={setIsActive} /></label>}
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={pending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export function BranchesPage() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const regions = useRegions();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [q, setQ] = useState('');
  const branches = useQuery({ queryKey: ['branches'], queryFn: async () => (await api.get<Branch[]>('/api/branches')).data });
  const regionName = (id: string) => regions.data?.find((r) => r.id === id)?.name ?? '—';

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/branches', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['branches'] }); success('Branch created'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not create branch')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) => api.patch(`/api/branches/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['branches'] }); success('Branch updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update branch')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/branches/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['branches'] }); success('Branch deleted'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not delete — staff or attendance records may still reference it')),
  });

  const needle = q.trim().toLowerCase();
  const filtered = (branches.data ?? []).filter((b) => !needle
    || b.name.toLowerCase().includes(needle) || b.code.toLowerCase().includes(needle)
    || b.address.toLowerCase().includes(needle) || (b.state ?? '').toLowerCase().includes(needle)
    || regionName(b.regionId).toLowerCase().includes(needle));

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Branches</h1><p className="mt-1 text-muted">{branches.data?.length ?? 0} branches</p></div>
        <button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button>
      </div>

      {open && <BranchForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}
      {editing && <BranchForm initial={editing} onSubmit={(v) => update.mutate({ id: editing.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} />}

      {!branches.isLoading && (branches.data?.length ?? 0) > 0 && (
        <SearchInput className="mb-4 max-w-sm" placeholder="Search by name, code, region, address, state…" value={q} onChange={(e) => setQ(e.target.value)} />
      )}

      {branches.isLoading && <Loader />}
      {!branches.isLoading && branches.data?.length === 0 && <EmptyState title="No branches yet" />}
      {!branches.isLoading && (branches.data?.length ?? 0) > 0 && filtered.length === 0 && <EmptyState title="No branches match your search" />}

      {filtered.length > 0 && (
        <div className="card overflow-x-auto !p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Code</th>
                <th className="px-4 py-3 font-semibold">Region</th>
                <th className="px-4 py-3 font-semibold">Address</th>
                <th className="px-4 py-3 font-semibold">State</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((branch) => (
                <tr key={branch.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><strong className={branch.isActive ? '' : 'text-muted line-through'}>{branch.name}</strong></td>
                  <td className="px-4 py-3 text-muted">{branch.code}</td>
                  <td className="px-4 py-3 text-muted">{regionName(branch.regionId)}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-muted" title={branch.address}>{branch.address}</td>
                  <td className="px-4 py-3 text-muted">{branch.state ?? '—'}</td>
                  <td className="px-4 py-3">{branch.isActive ? <span className="badge bg-green-50 text-green-700">Active</span> : <span className="badge bg-red-50 text-red-700">Inactive</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button className="btn-secondary !px-3" aria-label="Edit" onClick={() => { setEditing(branch); setOpen(false); }}><Pencil size={16} /></button>
                      <button className="btn-secondary !px-3" aria-label="Delete" onClick={() => { if (confirm(`Delete "${branch.name}"?`)) remove.mutate(branch.id); }}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
